import { initializeApp } from 'firebase/app';
import { getAuth } from 'firebase/auth';
import {
  clearIndexedDbPersistence,
  initializeFirestore,
  persistentLocalCache,
  persistentMultipleTabManager,
  terminate,
} from 'firebase/firestore';
import { connectFunctionsEmulator, getFunctions } from 'firebase/functions';
import { getStorage } from 'firebase/storage';

export class FirebaseService {
  private static Instance = initializeApp({
    apiKey: process.env.REACT_APP_API_KEY,
    authDomain: process.env.REACT_APP_AUTH_DOMAIN,
    databaseURL: process.env.REACT_APP_DATABASE_URL,
    projectId: process.env.REACT_APP_PROJECT_ID,
    storageBucket: process.env.REACT_APP_STORAGE_BUCKET,
    messagingSenderId: process.env.REACT_APP_MESSAGING_SENDER_ID,
  });

  public static Functions = getFunctions(this.Instance, 'us-central1');

  /** Persisted cache rather than the default in-memory one. Query targets keep
   * their resume token across reloads, so re-attaching a listener asks the
   * backend for changes since that token instead of replaying — and being
   * billed for — the entire result set. The multi-tab manager is required for
   * the PWA, where a second tab would otherwise fail to claim persistence.
   *
   * This must stay the only place a Firestore instance is created for this app.
   * `initializeFirestore` throws once one exists, and because it runs in a
   * static field initializer a stray `getFirestore()` evaluating first would
   * throw during module init, above any error boundary.
   *
   * The cache falls back to memory only for the conditions the SDK recognises —
   * FAILED_PRECONDITION and UNIMPLEMENTED, and DOMException quota-exceeded,
   * abort and Firefox private-browsing. Any other DOMException surfaces as a
   * listener error. The fallback is silent, so a session quietly running on the
   * memory cache looks identical to one on the persisted cache.
   *
   * Left on the SDK's default 40MB ceiling. LRU collection evicts inactive
   * targets, so admin one-shot reads can evict the resume tokens this is here
   * to keep; size it explicitly if that shows up in read volume. */
  public static Firestore = initializeFirestore(this.Instance, {
    localCache: persistentLocalCache({
      tabManager: persistentMultipleTabManager(),
    }),
  });

  public static Auth = getAuth(this.Instance);

  public static Storage = getStorage(this.Instance);

  /** Drops the persisted cache. Logging out only ends the Auth session, so
   * without this every document the session read stays readable on the device —
   * which would contradict the `browserSessionPersistence` login path, where
   * declining "remember me" is a statement that nothing should outlive the
   * session. Terminating leaves the instance unusable, hence the reload; it runs
   * even when clearing fails, so a failure cannot strand the app on a dead
   * instance. Clearing is refused while another tab holds the cache, so on a
   * multi-tab session the data survives until the last tab goes. */
  public static purgePersistedCache = async () => {
    try {
      await terminate(this.Firestore);
      await clearIndexedDbPersistence(this.Firestore);
    } catch (error) {
      console.error('Failed to clear the Firestore cache on logout: ', error);
    } finally {
      window.location.reload();
    }
  };
}

if (process.env.NODE_ENV === 'development') {
  const host = 'localhost';

  connectFunctionsEmulator(FirebaseService.Functions, host, 5001);
}
